import React, { useState } from 'react';
import { Col, Row } from '@/lib/utils';
import { DemiLightText, MediumText, RegularText } from '@/lib/Text';
import { Button } from '@/components/ui/button';
import { WorkTemplate } from '../types/workingHours';
import { TimeSelect } from './TimeSelect';
import SvgIcon from '@/components/SvgIcon';
import dayjs from 'dayjs';

type Props = {
    templates: WorkTemplate[];
    onCreate: (input: Omit<WorkTemplate, 'id'>) => void;
    onUpdate: (id: string, patch: Partial<Omit<WorkTemplate, 'id'>>) => void;
    onDelete: (id: string) => void;
    onClose: () => void;
};

const formatHhmm = (iso?: string) => (iso ? dayjs(iso).format('HH:mm') : '--:--');

export const WorkTemplateManager = ({ templates, onCreate, onUpdate, onDelete, onClose }: Props) => {
    const [editingId, setEditingId] = useState<string | 'new' | null>(null);
    const [draft, setDraft] = useState<Omit<WorkTemplate, 'id'>>({
        name: '',
        type: 'include',
        startTime: '',
        endTime: '',
        memo: '',
    });

    const startEdit = (t: WorkTemplate) => {
        setEditingId(t.id);
        setDraft({
            name: t.name,
            type: t.type,
            startTime: t.startTime ?? '',
            endTime: t.endTime ?? '',
            memo: t.memo ?? '',
        });
    };

    const startCreate = () => {
        setEditingId('new');
        setDraft({ name: '', type: 'include', startTime: '', endTime: '', memo: '' });
    };

    const cancelEdit = () => {
        setEditingId(null);
    };

    const isDraftValid = (): boolean => {
        if (!draft.name.trim()) return false;
        if (draft.type === 'include' && (!draft.startTime || !draft.endTime)) return false;
        return true;
    };

    const handleSave = () => {
        if (!isDraftValid()) return;
        const payload: Omit<WorkTemplate, 'id'> = {
            name: draft.name.trim(),
            type: draft.type,
            startTime: draft.type === 'include' ? draft.startTime : undefined,
            endTime: draft.type === 'include' ? draft.endTime : undefined,
            memo: draft.memo?.trim() || undefined,
        };
        if (editingId === 'new') {
            onCreate(payload);
        } else if (editingId) {
            onUpdate(editingId, payload);
        }
        setEditingId(null);
    };

    return (
        <div
            className="fixed inset-0 z-[99998] bg-black/30 flex items-center justify-center"
            onClick={(e) => {
                if (e.currentTarget === e.target) onClose();
            }}
        >
            <div
                className="bg-white rounded-[20px] shadow-lg w-[560px] max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <Col className="p-[24px]">
                    <Row className="items-center justify-between mb-[4px]">
                        <MediumText fontSize={20} color="#1A1A1A">
                            템플릿 관리
                        </MediumText>
                        <div className="cursor-pointer p-[6px]" onClick={onClose}>
                            <SvgIcon name="SvgX" size={14} stroke="#6B7280" strokeWidth={2} />
                        </div>
                    </Row>
                    <RegularText fontSize={13} color="#6B7280" className="mb-[16px]">
                        자주 쓰는 근무 패턴을 저장해 브러시로 사용할 수 있어요
                    </RegularText>

                    {/* 목록 */}
                    <Col className="gap-[8px]">
                        {templates.length === 0 && editingId !== 'new' && (
                            <div className="h-[80px] bg-[#F7F8FA] rounded-[10px] flex items-center justify-center">
                                <RegularText fontSize={13} color="#6B7280">
                                    저장된 템플릿이 없습니다
                                </RegularText>
                            </div>
                        )}

                        {templates.map((t) =>
                            editingId === t.id ? (
                                <TemplateEditRow
                                    key={t.id}
                                    draft={draft}
                                    setDraft={setDraft}
                                    isDraftValid={isDraftValid()}
                                    onSave={handleSave}
                                    onCancel={cancelEdit}
                                />
                            ) : (
                                <Row
                                    key={t.id}
                                    className="items-center justify-between rounded-[10px] px-[14px] py-[10px] hover:bg-[#F7F8FA]"
                                >
                                    <Row className="items-center gap-[10px]">
                                        <div
                                            className="w-[10px] h-[10px] rounded-full"
                                            style={{
                                                backgroundColor: t.type === 'include' ? '#0E8E50' : '#DC2E3E',
                                            }}
                                        />
                                        <Col>
                                            <RegularText fontSize={14} color="#1A1A1A">
                                                {t.name}
                                            </RegularText>
                                            <DemiLightText fontSize={13} color="#6B7280">
                                                {t.type === 'include'
                                                    ? `추가 근무 · ${formatHhmm(t.startTime)} ~ ${formatHhmm(t.endTime)}`
                                                    : '근무 제외'}
                                                {t.memo ? ` · ${t.memo}` : ''}
                                            </DemiLightText>
                                        </Col>
                                    </Row>
                                    <Row className="gap-[6px]">
                                        <Button
                                            variant="ghost"
                                            className="h-[32px] px-[10px] rounded-lg bg-[#F4F5F7] hover:bg-[#E8EAED]"
                                            onClick={() => startEdit(t)}
                                        >
                                            <RegularText fontSize={13}>편집</RegularText>
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            className="h-[32px] px-[10px] rounded-lg bg-[#FEE2E2] hover:bg-[#FECACA]"
                                            onClick={() => onDelete(t.id)}
                                        >
                                            <RegularText fontSize={13} color="#D93043">
                                                삭제
                                            </RegularText>
                                        </Button>
                                    </Row>
                                </Row>
                            ),
                        )}

                        {editingId === 'new' && (
                            <TemplateEditRow
                                draft={draft}
                                setDraft={setDraft}
                                isDraftValid={isDraftValid()}
                                onSave={handleSave}
                                onCancel={cancelEdit}
                            />
                        )}
                    </Col>

                    {/* 신규 버튼 */}
                    {editingId !== 'new' && (
                        <Button
                            variant="ghost"
                            className="h-[40px] mt-[14px] rounded-lg bg-[#E6EEFC] hover:bg-[#D8E3F9]"
                            onClick={startCreate}
                        >
                            <RegularText fontSize={13} color="#3D73DD">
                                + 새 템플릿 추가
                            </RegularText>
                        </Button>
                    )}

                    <Row className="justify-end mt-[20px]">
                        <Button
                            variant="default"
                            className="h-[40px] w-[96px] rounded-lg bg-[#3D73DD] hover:bg-[#3D73DD]/90 text-white"
                            onClick={onClose}
                        >
                            <RegularText fontSize={14} color="#FFFFFF">
                                닫기
                            </RegularText>
                        </Button>
                    </Row>
                </Col>
            </div>
        </div>
    );
};

const TemplateEditRow = ({
    draft,
    setDraft,
    isDraftValid,
    onSave,
    onCancel,
}: {
    draft: Omit<WorkTemplate, 'id'>;
    setDraft: React.Dispatch<React.SetStateAction<Omit<WorkTemplate, 'id'>>>;
    isDraftValid: boolean;
    onSave: () => void;
    onCancel: () => void;
}) => {
    return (
        <Col className="bg-[#F7F8FA] rounded-[12px] p-[14px] gap-[10px]">
            <input
                className="h-[40px] px-[12px] rounded-[10px] border border-solid border-[#E6E8EA] text-[14px] outline-none bg-white"
                placeholder="템플릿 이름 (예: 주말 당직, 생체 휴무)"
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            />
            <Row className="gap-[8px]">
                <TypeChip
                    active={draft.type === 'include'}
                    color="#0E8E50"
                    onClick={() => setDraft((d) => ({ ...d, type: 'include' }))}
                >
                    추가 근무 (IN)
                </TypeChip>
                <TypeChip
                    active={draft.type === 'exclude'}
                    color="#DC2E3E"
                    onClick={() => setDraft((d) => ({ ...d, type: 'exclude' }))}
                >
                    근무 제외 (OUT)
                </TypeChip>
            </Row>
            {draft.type === 'include' && (
                <Row className="items-center gap-[8px]">
                    <TimeSelect
                        value={draft.startTime ?? ''}
                        onChange={(val) => setDraft((d) => ({ ...d, startTime: val }))}
                        disabled={false}
                        compact
                        placeholder="시작"
                    />
                    <RegularText fontSize={14} color="#6B7280">
                        ~
                    </RegularText>
                    <TimeSelect
                        value={draft.endTime ?? ''}
                        onChange={(val) => setDraft((d) => ({ ...d, endTime: val }))}
                        disabled={false}
                        compact
                        allowDayEnd
                        placeholder="종료"
                    />
                </Row>
            )}
            <input
                className="h-[40px] px-[12px] rounded-[10px] border border-solid border-[#E6E8EA] text-[14px] outline-none bg-white"
                placeholder="메모 (선택)"
                value={draft.memo ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, memo: e.target.value }))}
            />
            <Row className="justify-end gap-[6px]">
                <Button
                    variant="ghost"
                    className="h-[34px] w-[72px] rounded-lg bg-[#F4F5F7] hover:bg-[#E8EAED]"
                    onClick={onCancel}
                >
                    <RegularText fontSize={13}>취소</RegularText>
                </Button>
                <Button
                    variant="default"
                    className={`h-[34px] w-[72px] rounded-lg text-white ${
                        isDraftValid ? 'bg-[#3D73DD] hover:bg-[#3D73DD]/90' : 'bg-[#3D73DD] opacity-40 cursor-not-allowed'
                    }`}
                    onClick={isDraftValid ? onSave : undefined}
                >
                    <RegularText fontSize={13} color="#FFFFFF">
                        저장
                    </RegularText>
                </Button>
            </Row>
        </Col>
    );
};

const TypeChip = ({
    active,
    color,
    onClick,
    children,
}: {
    active: boolean;
    color: string;
    onClick: () => void;
    children: React.ReactNode;
}) => (
    <div
        className={`h-[34px] px-[14px] rounded-[999px] flex items-center cursor-pointer border border-solid ${
            active ? 'border-[color:var(--chip-color)]' : 'border-transparent'
        } bg-white`}
        style={{ ['--chip-color' as any]: color, color: active ? color : '#6B7280' }}
        onClick={onClick}
    >
        <RegularText fontSize={13} color={active ? color : '#6B7280'}>
            {children}
        </RegularText>
    </div>
);
